import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// Helper: format INR using Intl (runtime may show rupee symbol)
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0
  );

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function Stat({ label, value, sub, highlight = false }) {
  return (
    <div style={{ borderRadius: 16, padding: 12, background: "rgba(255,255,255,0.95)", border: "1px solid #e6eef0" }}>
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function NumberInput({ label, value, onChange, step = 1, min, max, suffix = "" }) {
  return (
    <label style={{ display: "block", marginBottom: 8 }}>
      <div style={{ fontSize: 12, color: "#334155", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="number"
          value={value}
          step={step}
          min={min}
          max={max}
          onChange={(e) => onChange(Number(e.target.value || 0))}
          style={{ flex: 1, padding: 8, borderRadius: 10, border: "1px solid #e6eef0" }}
        />
        {suffix && <div style={{ alignSelf: "center", color: "#64748b" }}>{suffix}</div>}
      </div>
    </label>
  );
}

function SliderInput({ label, value, onChange, min = 0, max = 100, step = 1, suffix = "" }) {
  return (
    <label style={{ display: "block", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 12, color: "#334155" }}>{label}</div>
        <div style={{ fontSize: 12, color: "#64748b" }}>{value}{suffix}</div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: "100%" }} />
    </label>
  );
}

// Template storage helpers (localStorage)
const TEMPLATE_KEY = "lead_lp_templates_v1";
const sanitizeFileName = (s) => (s || "template").toLowerCase().replace(/[^a-z0-9-_]+/g, "-").replace(/^-+|-+$/g, "");

function loadTemplates() {
  try {
    const raw = localStorage.getItem(TEMPLATE_KEY) || "[]";
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}
function saveTemplates(arr) {
  try {
    localStorage.setItem(TEMPLATE_KEY, JSON.stringify(arr));
  } catch (e) {
    // ignore
  }
}

export default function App() {
  // Inputs
  const [leadsPerDayT, setLeadsPerDayT] = useState(150);
  const [leadsPerDayM, setLeadsPerDayM] = useState(150);
  const [workDays, setWorkDays] = useState(26);
  const [cpl, setCpl] = useState(87.38);
  const [convT, setConvT] = useState(20);
  const [convM, setConvM] = useState(30);
  const [delivery, setDelivery] = useState(40);
  const [price, setPrice] = useState(1700);
  const [prodCost, setProdCost] = useState(500);
  const [shipCost, setShipCost] = useState(275);
  const [salaryPer, setSalaryPer] = useState(25000);
  const [headcount, setHeadcount] = useState(20);

  // Templates
  const [templateName, setTemplateName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setTemplates(loadTemplates());
  }, []);

  const inputsObject = useMemo(() => ({ leadsPerDayT, leadsPerDayM, workDays, cpl, convT, convM, delivery, price, prodCost, shipCost, salaryPer, headcount }), [leadsPerDayT, leadsPerDayM, workDays, cpl, convT, convM, delivery, price, prodCost, shipCost, salaryPer, headcount]);

  // Core calculations
  const calc = useMemo(() => {
    const pT = clamp(convT / 100, 0, 1);
    const pM = clamp(convM / 100, 0, 1);
    const s = clamp(delivery / 100, 0, 1);

    const maxT = leadsPerDayT * workDays;
    const maxM = leadsPerDayM * workDays;
    const unitCost = prodCost + shipCost;

    const alphaFor = (p) => p * (s * price - unitCost) - cpl;
    const alphaT = alphaFor(pT);
    const alphaM = alphaFor(pM);

    const takeT = alphaT > 0 ? maxT : 0;
    const takeM = alphaM > 0 ? maxM : 0;

    const shippedT = pT * takeT;
    const shippedM = pM * takeM;
    const shipped = shippedT + shippedM;
    const delivered = s * shipped;

    const revenue = price * delivered;
    const variableCost = prodCost * delivered + shipCost * shipped + cpl * (takeT + takeM);
    const fixed = salaryPer * headcount;
    const profit = revenue - variableCost - fixed;

    const contribution = revenue - unitCost * shipped - cpl * (takeT + takeM);
    const leadsTaken = takeT + takeM;
    const avgAlpha = leadsTaken > 0 ? contribution / leadsTaken : 0;

    const deficit = profit < 0 ? -profit : 0;
    const dPdS = price * shipped;
    const dPdPrice = s * shipped;
    const cplPerLead = leadsTaken;
    const unitPerOrder = shipped;

    const dsNeeded = dPdS > 0 ? deficit / dPdS : Infinity;
    const priceUpNeeded = dPdPrice > 0 ? deficit / dPdPrice : Infinity;
    const cplDownNeeded = cplPerLead > 0 ? deficit / cplPerLead : Infinity;
    const unitReduceNeeded = unitPerOrder > 0 ? deficit / unitPerOrder : Infinity;

    return {
      pT,
      pM,
      s,
      maxT,
      maxM,
      takeT,
      takeM,
      shipped,
      delivered,
      revenue,
      variableCost,
      fixed,
      profit,
      contribution,
      leadsTaken,
      avgAlpha,
      deficit,
      dsNeeded,
      priceUpNeeded,
      cplDownNeeded,
      unitReduceNeeded,
      alphaT,
      alphaM,
      unitCost,
    };
  }, [leadsPerDayT, leadsPerDayM, workDays, cpl, convT, convM, delivery, price, prodCost, shipCost, salaryPer, headcount]);

  // snapshot compare
  const [snapA, setSnapA] = useState(null);
  const [snapB, setSnapB] = useState(null);
  const takeSnapshot = () => ({ inputs: inputsObject, outputs: { revenue: calc.revenue, variableCost: calc.variableCost, fixed: calc.fixed, profit: calc.profit, shipped: calc.shipped, delivered: calc.delivered, alphaT: calc.alphaT, alphaM: calc.alphaM } });

  // template helpers
  const saveTemplate = () => {
    const item = { id: Date.now().toString(36), name: templateName || `template-${Date.now()}`, createdAt: new Date().toISOString(), inputs: inputsObject, outputs: { revenue: calc.revenue, variableCost: calc.variableCost, fixed: calc.fixed, profit: calc.profit } };
    const updated = [item, ...templates];
    setTemplates(updated);
    saveTemplates(updated);
    setSelectedId(item.id);
  };
  const exportJSON = (obj, filename) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const exportCurrent = () => exportJSON({ name: templateName || "template", inputs: inputsObject, outputs: { revenue: calc.revenue, variableCost: calc.variableCost, fixed: calc.fixed, profit: calc.profit } }, `${sanitizeFileName(templateName || "template")}.json`);
  const exportAll = () => exportJSON(templates, "lead-lp-templates-all.json");
  const importFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const data = JSON.parse(r.result);
        if (Array.isArray(data)) {
          const merged = [...data, ...templates];
          setTemplates(merged);
          saveTemplates(merged);
        } else if (data && data.inputs) {
          const one = [{ ...data, id: Date.now().toString(36), createdAt: new Date().toISOString() }, ...templates];
          setTemplates(one);
          saveTemplates(one);
          // apply immediately
          const inp = data.inputs;
          setLeadsPerDayT(inp.leadsPerDayT || leadsPerDayT);
          setLeadsPerDayM(inp.leadsPerDayM || leadsPerDayM);
          setWorkDays(inp.workDays || workDays);
          setCpl(inp.cpl || cpl);
          setConvT(inp.convT || convT);
          setConvM(inp.convM || convM);
          setDelivery(inp.delivery || delivery);
          setPrice(inp.price || price);
          setProdCost(inp.prodCost || prodCost);
          setShipCost(inp.shipCost || shipCost);
          setSalaryPer(inp.salaryPer || salaryPer);
          setHeadcount(inp.headcount || headcount);
          setTemplateName(data.name || templateName);
        } else {
          alert("Invalid JSON file for templates.");
        }
      } catch (err) {
        alert("Could not parse JSON file.");
      } finally {
        e.target.value = "";
      }
    };
    r.readAsText(f);
  };
  const applySelected = () => {
    const t = templates.find((x) => x.id === selectedId);
    if (t && t.inputs) {
      const inp = t.inputs;
      setLeadsPerDayT(inp.leadsPerDayT || leadsPerDayT);
      setLeadsPerDayM(inp.leadsPerDayM || leadsPerDayM);
      setWorkDays(inp.workDays || workDays);
      setCpl(inp.cpl || cpl);
      setConvT(inp.convT || convT);
      setConvM(inp.convM || convM);
      setDelivery(inp.delivery || delivery);
      setPrice(inp.price || price);
      setProdCost(inp.prodCost || prodCost);
      setShipCost(inp.shipCost || shipCost);
      setSalaryPer(inp.salaryPer || salaryPer);
      setHeadcount(inp.headcount || headcount);
      setTemplateName(t.name || templateName);
    }
  };
  const deleteSelected = () => {
    const updated = templates.filter((x) => x.id !== selectedId);
    setTemplates(updated);
    saveTemplates(updated);
    setSelectedId(updated[0] ? updated[0].id : "");
  };

  // Basic developer assertions (kept as runtime checks)
  useEffect(() => {
    const alpha = (p, s, price, unit, cpl) => p * (s * price - unit) - cpl;
    const a1 = alpha(0.2, 0.4, 1700, prodCost + shipCost, 87.38);
    console.assert(a1 < 0, "alpha should be negative in low-DSR high-CPL test");
  }, [prodCost, shipCost]);

  // Chart data
  const chartData = [
    { name: "Revenue", A: snapA ? Math.round(snapA.outputs.revenue || 0) : 0, B: snapB ? Math.round(snapB.outputs.revenue || 0) : 0 },
    { name: "Variable", A: snapA ? Math.round(snapA.outputs.variableCost || 0) : 0, B: snapB ? Math.round(snapB.outputs.variableCost || 0) : 0 },
    { name: "Fixed", A: snapA ? Math.round(snapA.outputs.fixed || 0) : 0, B: snapB ? Math.round(snapB.outputs.fixed || 0) : 0 },
    { name: "Profit", A: snapA ? Math.round(snapA.outputs.profit || 0) : 0, B: snapB ? Math.round(snapB.outputs.profit || 0) : 0 },
  ];

  return (
    <div style={{ minHeight: "100vh", padding: 24, background: "#f0fdf4", color: "#0f172a" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Lead Funnel LP Optimizer</h1>
        <p style={{ color: "#475569" }}>Interactive optimizer. Change inputs on the left, see outputs on the right.</p>

        <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 20, marginTop: 20 }}>
          <div>
            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Volumes & Pricing</h3>
              <NumberInput label="Leads/Day - Tamil" value={leadsPerDayT} onChange={setLeadsPerDayT} min={0} />
              <NumberInput label="Leads/Day - Malayalam" value={leadsPerDayM} onChange={setLeadsPerDayM} min={0} />
              <NumberInput label="Working Days / Month" value={workDays} onChange={setWorkDays} min={1} />
              <NumberInput label="Cost per Lead (INR)" value={cpl} onChange={setCpl} min={0} step={0.01} />
              <NumberInput label="Avg Selling Price (INR)" value={price} onChange={setPrice} min={0} />
            </div>

            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Rates & Success</h3>
              <SliderInput label="Conversion - Tamil" value={convT} onChange={setConvT} min={0} max={100} />
              <SliderInput label="Conversion - Malayalam" value={convM} onChange={setConvM} min={0} max={100} />
              <SliderInput label="Delivery Success" value={delivery} onChange={setDelivery} min={0} max={100} />
            </div>

            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Costs</h3>
              <NumberInput label="Production Cost / Shipped (INR)" value={prodCost} onChange={setProdCost} min={0} />
              <NumberInput label="Shipping (INR)" value={shipCost} onChange={setShipCost} min={0} />
              <NumberInput label="Salary / Person (INR)" value={salaryPer} onChange={setSalaryPer} min={0} />
              <NumberInput label="Headcount" value={headcount} onChange={setHeadcount} min={0} />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setSnapA(takeSnapshot())} style={{ padding: "8px 12px", borderRadius: 8, background: "#10b981", color: "white", border: "none", cursor: "pointer" }}>Save as Value 1 (A)</button>
              <button onClick={() => setSnapB(takeSnapshot())} style={{ padding: "8px 12px", borderRadius: 8, background: "#0ea5e9", color: "white", border: "none", cursor: "pointer" }}>Save as Value 2 (B)</button>
              <button onClick={() => { setLeadsPerDayT(150); setLeadsPerDayM(150); setWorkDays(26); setCpl(87.38); setConvT(20); setConvM(30); setDelivery(40); setPrice(1700); setProdCost(500); setShipCost(275); setSalaryPer(25000); setHeadcount(20); }} style={{ marginLeft: "auto", padding: "8px 12px", borderRadius: 8, border: "1px solid #ccc", background: "#f8f9fa", cursor: "pointer" }}>Reset</button>
            </div>

            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginTop: 12 }}>
              <h3 style={{ margin: 0 }}>Templates</h3>
              <div style={{ marginTop: 8 }}>
                <input placeholder="Template name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e6eef0" }} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={saveTemplate} style={{ padding: "8px 10px", borderRadius: 8, background: "#0f172a", color: "white", border: "none", cursor: "pointer" }}>Save</button>
                <button onClick={exportCurrent} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", background: "#f8f9fa", cursor: "pointer" }}>Export JSON</button>
                <button onClick={() => fileRef.current && fileRef.current.click()} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", background: "#f8f9fa", cursor: "pointer" }}>Import JSON</button>
                <input ref={fileRef} type="file" accept="application/json" onChange={importFile} style={{ display: "none" }} />
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ flex: 1, padding: 8 }}>
                  <option value="">Select saved template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} - {new Date(t.createdAt).toLocaleString()}</option>
                  ))}
                </select>
                <button onClick={applySelected} style={{ padding: "8px 10px", borderRadius: 8, background: "#10b981", color: "white", border: "none", cursor: "pointer" }}>Apply</button>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => { const t = templates.find(x => x.id === selectedId); if (t) exportJSON(t, `${sanitizeFileName(t.name)}.json`); }} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", background: "#f8f9fa", cursor: "pointer" }}>Export Selected</button>
                <button onClick={exportAll} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", background: "#f8f9fa", cursor: "pointer" }}>Export All</button>
                <button onClick={deleteSelected} style={{ padding: "8px 10px", borderRadius: 8, background: "#ef4444", color: "white", border: "none", cursor: "pointer" }}>Delete</button>
              </div>

              <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                JSON contains inputs and computed outputs. Import accepts a single template object or an array of templates.
              </div>
            </div>
          </div>

          <div>
            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>LP Decision & Throughput</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12 }}>
                <Stat label="Per-Lead alpha (Tamil)" value={fmtINR(calc.alphaT)} sub="Contribution per Tamil lead" />
                <Stat label="Per-Lead alpha (Malayalam)" value={fmtINR(calc.alphaM)} sub="Contribution per Malayalam lead" />
                <Stat label="Monthly Leads (Tamil)" value={calc.takeT.toLocaleString()} sub={calc.alphaT > 0 ? "Taking all available" : "LP sets to 0"} />
                <Stat label="Monthly Leads (Malayalam)" value={calc.takeM.toLocaleString()} sub={calc.alphaM > 0 ? "Taking all available" : "LP sets to 0"} />
                <Stat label="Orders Shipped" value={Math.round(calc.shipped).toLocaleString()} />
                <Stat label="Delivered Orders" value={Math.round(calc.delivered).toLocaleString()} sub={`Success ${Math.round(calc.s * 100)}%`} />
              </div>
            </div>

            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>P&L Summary (Monthly)</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12 }}>
                <Stat label="Revenue" value={fmtINR(calc.revenue)} />
                <Stat label="Variable Costs" value={fmtINR(calc.variableCost)} sub={`Unit shipped cost: ${fmtINR(calc.unitCost)}`} />
                <Stat label="Fixed Costs" value={fmtINR(calc.fixed)} />
                <Stat label="Profit" value={fmtINR(calc.profit)} />
              </div>
            </div>

            <div style={{ background: "#fff", padding: 12, borderRadius: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Breakeven Insight</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 12 }}>
                <Stat label="Operating Contribution" value={fmtINR(calc.contribution)} />
                <Stat label="Avg alpha per Lead" value={fmtINR(calc.avgAlpha)} />
                <Stat label="Leads needed to breakeven" value={calc.avgAlpha > 0 ? Math.ceil(calc.fixed / calc.avgAlpha).toLocaleString() : "-"} />
                <Stat label="Shortfall vs fixed" value={fmtINR(calc.profit < 0 ? -calc.profit : 0)} />
              </div>

              {calc.profit < 0 && (calc.alphaT > 0 || calc.alphaM > 0) && (
                <div style={{ marginTop: 12, color: "#334155" }}>
                  Unit contribution is positive but total volume is insufficient to cover fixed costs. Short by {fmtINR(-calc.profit)}.
                  <ul>
                    <li>Increase delivery success by ~{Number.isFinite(calc.dsNeeded) ? (calc.dsNeeded * 100).toFixed(2) : "-"} percentage points</li>
                    <li>or raise average price by INR {Number.isFinite(calc.priceUpNeeded) ? Math.ceil(calc.priceUpNeeded) : "-"}</li>
                    <li>or reduce CPL by INR {Number.isFinite(calc.cplDownNeeded) ? Math.ceil(calc.cplDownNeeded) : "-"} per lead</li>
                    <li>or reduce unit shipped cost by INR {Number.isFinite(calc.unitReduceNeeded) ? Math.ceil(calc.unitReduceNeeded) : "-"} per order</li>
                  </ul>
                </div>
              )}
            </div>

            <div style={{ background: "#fff", padding: 12, borderRadius: 12 }}>
              <h3 style={{ margin: 0 }}>Scenario Visualizer</h3>
              <div style={{ height: 200, marginTop: 12 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => fmtINR(v)} />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Legend />
                    <Bar dataKey="A" name="Value 1" fill="#10b981" />
                    <Bar dataKey="B" name="Value 2" fill="#0ea5e9" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <div style={{ flex: 1, background: "#f8fafc", padding: 8, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Value 1</div>
                  {snapA ? (
                    <div style={{ fontSize: 12 }}>
                      <div>Delivered: {Math.round(snapA.outputs.delivered || 0)}</div>
                      <div>Profit: {fmtINR(snapA.outputs.profit || 0)}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#64748b" }}>Save snapshot A</div>
                  )}
                </div>

                <div style={{ flex: 1, background: "#f8fafc", padding: 8, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Value 2</div>
                  {snapB ? (
                    <div style={{ fontSize: 12 }}>
                      <div>Delivered: {Math.round(snapB.outputs.delivered || 0)}</div>
                      <div>Profit: {fmtINR(snapB.outputs.profit || 0)}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#64748b" }}>Save snapshot B</div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
